import { Grade } from "./useGameStore";

// Timing windows (seconds from ideal beat)
export const PERFECT_TIMING_WINDOW = 0.1;
export const GREAT_TIMING_WINDOW = 0.22;
export const GOOD_TIMING_WINDOW = 0.28;

export function getGrade(timingError: number): Grade {
  if (timingError < PERFECT_TIMING_WINDOW) return "PERFECT";
  if (timingError < GREAT_TIMING_WINDOW) return "GREAT";
  if (timingError < GOOD_TIMING_WINDOW) return "GOOD";
  return "LATE";
}

export const IPPON_TIMING_WINDOW = 0.065;
// Tenouchi "snap" thresholds — these gate the GRADE CAP (can this cut even
// reach GREAT/PERFECT/IPPON), a different job from TENOUCHI_SNAP_THRESHOLD
// below (the standalone ×1.25 bonus + visual marker). 2026-07-11: a previous
// pass raised BOTH concepts by the same ~2.3x factor, which accidentally made
// GREAT itself much harder to reach (it's already gated by three OTHER
// independent axes — timing/direction/center — so snap didn't need to carry
// that weight too). Confirmed by real play: the player cleared the *harder*
// standalone bonus threshold 162 times in one run, so snap was never the
// bottleneck for grade. These caps are back down (GREAT below its
// pre-reformulation value, since GREAT is meant to be the easy ruler now) —
// do not raise these in lockstep with TENOUCHI_SNAP_THRESHOLD again.
export const IPPON_TENOUCHI_SNAP = 3.2;
export const PERFECT_TENOUCHI_SNAP = 2.3;
export const GREAT_TENOUCHI_SNAP = 1.4;
export const IPPON_DIRECTION_DOT_THRESHOLD = 0.9; // ~26deg cone
export const PERFECT_DIRECTION_DOT_THRESHOLD = 0.78; // ~39deg cone
export const GREAT_DIRECTION_DOT_THRESHOLD = 0.45; // ~63deg cone
export const IPPON_CENTER_ACCURACY = 0.7;
export const PERFECT_CENTER_ACCURACY = 0.52;
export const GREAT_CENTER_ACCURACY = 0.2;
export const GOOD_CENTER_ACCURACY = 0.15;

interface CutQuality {
  timingError: number;
  snap: number;
  directionAlignment: number;
  centerAccuracy: number;
}

export function isIpponCut({
  timingError,
  snap,
  directionAlignment,
  centerAccuracy,
}: CutQuality): boolean {
  return (
    timingError < IPPON_TIMING_WINDOW &&
    snap >= IPPON_TENOUCHI_SNAP &&
    directionAlignment >= IPPON_DIRECTION_DOT_THRESHOLD &&
    centerAccuracy >= IPPON_CENTER_ACCURACY
  );
}

const GRADE_RANK: Record<Grade, number> = {
  IPPON: 5,
  PERFECT: 4,
  GREAT: 3,
  GOOD: 2,
  LATE: 1,
  MISS: 0,
};

function capGrade(grade: Grade, cap: Grade): Grade {
  return GRADE_RANK[grade] <= GRADE_RANK[cap] ? grade : cap;
}

function directionGradeCap(directionAlignment: number): Grade {
  if (directionAlignment >= PERFECT_DIRECTION_DOT_THRESHOLD) return "PERFECT";
  if (directionAlignment >= GREAT_DIRECTION_DOT_THRESHOLD) return "GREAT";
  return "GOOD";
}

function centerGradeCap(centerAccuracy: number): Grade {
  if (centerAccuracy >= PERFECT_CENTER_ACCURACY) return "PERFECT";
  if (centerAccuracy >= GREAT_CENTER_ACCURACY) return "GREAT";
  if (centerAccuracy >= GOOD_CENTER_ACCURACY) return "GOOD";
  return "LATE";
}

function snapGradeCap(snap: number): Grade {
  if (snap >= PERFECT_TENOUCHI_SNAP) return "PERFECT";
  if (snap >= GREAT_TENOUCHI_SNAP) return "GREAT";
  return "GOOD";
}

export function getCutGrade(quality: CutQuality): Grade {
  if (isIpponCut(quality)) return "IPPON";

  let grade = getGrade(quality.timingError);
  grade = capGrade(grade, directionGradeCap(quality.directionAlignment));
  grade = capGrade(grade, centerGradeCap(quality.centerAccuracy));
  grade = capGrade(grade, snapGradeCap(quality.snap));
  return grade;
}

// GOOD and LATE recalibrated 2026-07-11: GOOD used to be exactly half of
// GREAT, so a realistic mix of GOOD/GREAT hits (rather than an all-GREAT
// clear) earned far less than the campaign thresholds assume. LATE used to
// be worth almost nothing, over-punishing an honestly-aimed cut that's just
// outside the tighter timing windows (common in faster phases).
export const BASE_POINTS: Record<Grade, number> = {
  IPPON: 250,
  PERFECT: 100,
  GREAT: 60,
  GOOD: 42,
  LATE: 20,
  MISS: 0,
};

// Small authored-direction bonus. The grade itself carries most of the score
// weight so LATE/GOOD/GREAT/PERFECT remain distinct.
export const DIRECTION_BONUS = 20;

export function comboMultiplier(combo: number): number {
  if (combo >= 50) return 5;
  if (combo >= 20) return 4;
  if (combo >= 10) return 3;
  if (combo >= 5) return 2;
  return 1;
}

// Tenouchi: the snap (squeeze/acceleration) at moment of impact rewards a
// sharp finish, not sustained fast flailing — see the comment above
// IPPON_TENOUCHI_SNAP for how this is measured.
export const TENOUCHI_SNAP_THRESHOLD = 4.1;

export function tenouchiBonus(snap: number): number {
  return snap >= TENOUCHI_SNAP_THRESHOLD ? 1.25 : 1.0;
}

export const GRADE_COLORS: Record<Grade, string> = {
  IPPON: "#ffffff",
  PERFECT: "#ffdd00",
  GREAT: "#00ff88",
  GOOD: "#74b9ff",
  LATE: "#ff8800",
  MISS: "#ff3333",
};
